/**
 * File Service Utilities
 *
 * Centralized utilities for file operations including preview and download.
 * Files are stored in AWS S3 and accessed through the backend proxy.
 */

/**
 * Generate the correct URL for file preview
 * @param fileId - The file ID
 * @returns The preview URL that streams the file from backend
 */
export function getFilePreviewUrl(fileId: number | string): string {
  // Use the dedicated file endpoint which handles S3 streaming
  // preview=true sets Content-Disposition to inline for browser display
  return `/api/files/${fileId}/download?preview=true`;
}

/**
 * Generate the correct URL for file download
 * @param fileId - The file ID
 * @returns The download URL that forces browser download
 */
export function getFileDownloadUrl(fileId: number | string): string {
  // Use the dedicated file endpoint which handles S3 streaming
  // preview=false (or omitted) sets Content-Disposition to attachment
  return `/api/files/${fileId}/download?preview=false`;
}

/**
 * Open a file for preview in a new tab
 * @param fileId - The file ID
 */
export function previewFile(fileId: number | string): void {
  const url = getFilePreviewUrl(fileId);
  window.open(url, '_blank');
}

/**
 * Trigger a file download
 * @param fileId - The file ID
 */
export function downloadFile(fileId: number | string): void {
  const url = getFileDownloadUrl(fileId);
  // Open in new tab - the attachment disposition will force download
  window.open(url, '_blank');
}

/**
 * Office document MIME types that preview inline via the download-preview path — the backend converts
 * them to PDF on the fly (`GET /api/files/:id/download?preview=true`). Kept in step with the backend
 * convertible-type set (`backend/src/files/utils/office-document.util.ts`): widening one without the
 * other either hides the capability or promises a preview the backend won't produce.
 */
const PREVIEWABLE_OFFICE_MIME_TYPES = new Set([
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
  'application/docx', // .docx (loose variant seen on some uploads)
  'application/msword', // .doc
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
  'application/vnd.openxmlformats-officedocument.presentationml.presentation', // .pptx
  'application/vnd.oasis.opendocument.text' // .odt
]);

/**
 * Check if a file can be previewed in the browser
 * @param mimeType - The file's MIME type
 * @returns Whether the file can be previewed
 */
export function canPreviewFile(mimeType?: string | null): boolean {
  if (!mimeType) return false;

  // PDFs and images preview inline directly; office documents preview via server-side PDF conversion.
  return (
    mimeType === 'application/pdf' ||
    mimeType.startsWith('image/') ||
    PREVIEWABLE_OFFICE_MIME_TYPES.has(mimeType.toLowerCase())
  );
}

/**
 * Browser-previewable file extensions (lowercase, no dot) — the extension-keyed sibling of
 * `canPreviewFile`. Includes the office types the backend converts to PDF on the preview path.
 */
const PREVIEWABLE_EXTENSIONS = new Set([
  'pdf',
  'png',
  'jpg',
  'jpeg',
  'gif',
  'webp',
  'svg',
  'bmp',
  'heic',
  'heif',
  // Office documents — the backend converts these to PDF on the download-preview path.
  'docx',
  'doc',
  'xlsx',
  'pptx',
  'odt'
]);

/**
 * Whether a file can be previewed inline in the browser, decided from its extension rather than its
 * MIME type — the sibling of {@link canPreviewFile} for callers that only have the extension (e.g.
 * the grant cheatsheet, where the BFF surfaces each reference document's extension). PDFs, images,
 * and LibreOffice-convertible office documents (`.docx`, `.doc`, `.xlsx`, `.pptx`, `.odt`) preview
 * inline; genuinely unconvertible types (`.msg`, archives) must be downloaded instead.
 *
 * @param extension - The file extension, with or without a leading dot (case-insensitive).
 */
export function isPreviewableExtension(extension?: string | null): boolean {
  if (!extension) return false;
  return PREVIEWABLE_EXTENSIONS.has(extension.replace(/^\./, '').toLowerCase());
}
